import type { ComputedRef, Ref } from 'vue';
import { CfgToastNotificationType } from '@cfgtech/ui';
import type { Update } from '~/services/generated/client';
import { notify } from '~/composables/useToast';

export type CompanyId = string;
export type IssueId = string;

export interface UseEventsContext {
  companyId?: Ref<CompanyId | null> | ComputedRef<CompanyId | null>;
  issueId?: Ref<IssueId | null> | ComputedRef<IssueId | null>;
}

export interface UseEventsOptions {
  lazy?: boolean;
}

interface Pagination {
  page: number;
  pageSize: number;
  count?: number;
}

const initialPagination: Pagination = {
  page: 1,
  pageSize: 10,
};

export { type Update as Events };

const getKey = (companyId: CompanyId | null, issueId: IssueId | null) => {
  let key = 'events';

  if (companyId) {
    key += `_company_${companyId}`;
  }

  if (issueId) {
    key += `_issue_${issueId}`;
  }

  return key;
};

export function useEvents({ companyId, issueId }: UseEventsContext, options?: UseEventsOptions) {
  const { handleAsyncOperation } = useAsyncOperationHandler();
  const userState = useUserState();
  const { investorActualityApi } = useApi();
  const { t } = useI18n();

  const areEventsOpened = useState<boolean>('areEventsOpened', () => false);

  const key = getKey(companyId?.value || null, issueId?.value || null);

  const totalEvents = useState<number>('total_events', () => 0);
  const totalCompanyEvents = useState<number>('total_company_events', () => 0);
  const events = useState<Update[]>(key, () => []);
  const isLoading = useState<boolean>(`${key}_pending`, () => false);
  const isLoadMoreLoading = useState<boolean>(`${key}_load_more_pending`, () => false);
  const pagination = ref<Pagination>({ ...initialPagination });

  const abortController = useState<AbortController | null>('eventsAbortController', () => null);

  async function baseFetchEvents(
    { pagination: { page, pageSize } }: { pagination: Pagination },
    { abortController }: { abortController: AbortController | null }
  ) {
    return await investorActualityApi.value.listInvestorUpdates({
      xCFGINVESTORUSERID: userState.value?.sub || '',
      companyId: companyId?.value ? String(companyId.value) : undefined,
      isin: issueId?.value || undefined,
      page,
      pageSize,
    }, {
      signal: abortController?.signal,
    });
  }

  async function fetchEvents() {
    abortController.value?.abort?.();
    abortController.value = new AbortController();

    try {
      const { count, results } = await baseFetchEvents(
        { pagination: pagination.value },
        { abortController: abortController.value }
      );

      pagination.value.count = count;
      return results;
    } catch (err: any) {
      if (err?.cause?.name === 'AbortError') {
        return;
      }

      notify({
        title: t('events.error.title'),
        text: '',
        type: CfgToastNotificationType.Error,
      });

      return [];
    }
  }

  // Reset pagination on unmount
  tryOnBeforeUnmount(() => {
    events.value = [];
  });

  watch(pagination, (currentPagination, prevPagination) => {
    if (!prevPagination) {
      fetchInitialEvents();
      return;
    }

    if (!isEqual(currentPagination, prevPagination)) {
      loadMoreEvents();
    }
  }, { immediate: !options?.lazy });

  async function loadMoreEvents() {
    isLoadMoreLoading.value = true;

    pagination.value.page += 1;

    const loadedEvents = await fetchEvents();
    events.value.push(...loadedEvents || []);

    isLoadMoreLoading.value = false;
  }

  async function fetchInitialEvents() {
    isLoading.value = true;

    const loadedEvents = await fetchEvents();
    events.value = loadedEvents || [];
    isLoading.value = false;
  }

  async function prefetchTotalEvents() {
    try {
      const { count } = await baseFetchEvents(
        { pagination: { page: 1, pageSize: 1 } },
        { abortController: null }
      );

      totalEvents.value = count || 0;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  const prefetchTotalEventsByIsin = (isin: string) => handleAsyncOperation(
    async () => {
      const { count } = await investorActualityApi.value.listInvestorUpdates({
        xCFGINVESTORUSERID: userState.value?.sub || '',
        isin,
      }, {
        signal: abortController.value?.signal,
      });
      totalCompanyEvents.value = count || 0;
    },
    { errorTitleKey: 'events.error.title' },
  );

  return {
    events: computed(() => events.value),
    isLoading: readonly(isLoading),
    leftEventsToLoad: computed(() => (pagination.value.count || 0) - events.value.length),
    isLoadMoreLoading,
    totalEvents,
    totalCompanyEvents,
    areEventsOpened,
    prefetchTotalEvents,
    prefetchTotalEventsByIsin,

    pagination: computed({
      get: () => pagination.value,
      set(newPagination: Partial<Pagination>) {
        pagination.value = { ...pagination.value, ...newPagination };
      }
    }),

    // Methods
    fetchInitialEvents,
    loadMoreEvents,
    clear() {
      events.value = [];
      pagination.value = { ...initialPagination };
    }
  };
}
